import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { Mic, X, Send, Trash2, Pause, Play, StopCircle, Volume2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { VoiceRecorder as VoiceRecorderUtil } from '@/utils/voice-recorder';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';

interface VoiceRecorderProps {
  roomName: string;
  entityId?: string;
  onRecordingComplete?: (messageId: string, attachmentData?: any) => void;
  onSendMessage?: (text: string, metadata?: any) => Promise<void>;
  onCancel?: () => void;
  className?: string;
  disabled?: boolean;
}

interface RecordingState {
  isRecording: boolean;
  isPaused: boolean;
  isUploading: boolean;
  isPlaying: boolean;
  duration: number;
  amplitude: number;
  blob: Blob | null;
  waveform: number[];
  playbackProgress: number;
}

export const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  roomName,
  entityId,
  onRecordingComplete,
  onSendMessage,
  onCancel,
  className,
  disabled = false,
}) => {
  const [state, setState] = useState<RecordingState>({
    isRecording: false,
    isPaused: false,
    isUploading: false,
    isPlaying: false,
    duration: 0,
    amplitude: 0,
    blob: null,
    waveform: [],
    playbackProgress: 0,
  });

  const [isHolding, setIsHolding] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [liveAmplitudeBars, setLiveAmplitudeBars] = useState<number[]>(new Array(30).fill(0));
  const recorderRef = useRef<VoiceRecorderUtil | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const playbackIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const holdTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const amplitudeHistoryRef = useRef<number[]>([]);
  const { toast } = useToast();

  // Format duration as MM:SS
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle recording start
  const startRecording = useCallback(async () => {
    if (!VoiceRecorderUtil.isSupported()) {
      toast({
        title: 'Not supported',
        description: 'Voice recording is not supported on this browser',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Reset amplitude history and bars
      amplitudeHistoryRef.current = [];
      setLiveAmplitudeBars(new Array(30).fill(0));

      recorderRef.current = new VoiceRecorderUtil({
        maxDuration: 300, // 5 minutes max
        onStart: () => {
          setState(prev => ({ ...prev, isRecording: true, duration: 0 }));

          // Start duration counter
          durationIntervalRef.current = setInterval(() => {
            setState(prev => ({ ...prev, duration: prev.duration + 1 }));
          }, 1000);
        },
        onAmplitudeUpdate: (amplitude) => {
          setState(prev => ({ ...prev, amplitude }));

          // Update live amplitude bars with rolling effect
          setLiveAmplitudeBars(prev => {
            const newBars = [...prev];
            // Shift bars to the left
            for (let i = 0; i < newBars.length - 1; i++) {
              newBars[i] = newBars[i + 1];
            }
            // Add new amplitude to the end
            newBars[newBars.length - 1] = amplitude;
            return newBars;
          });

          // Store amplitude for waveform generation
          amplitudeHistoryRef.current.push(amplitude);
        },
        onStop: async (blob, duration) => {
          // Clear duration interval
          if (durationIntervalRef.current) {
            clearInterval(durationIntervalRef.current);
            durationIntervalRef.current = null;
          }

          // Generate waveform
          try {
            const waveform = await VoiceRecorderUtil.generateWaveform(blob, 50);
            setState(prev => ({
              ...prev,
              isRecording: false,
              blob,
              duration: Math.floor(duration),
              waveform,
            }));
            setShowPreview(true);
          } catch (error) {
            console.error('Error generating waveform:', error);
            setState(prev => ({
              ...prev,
              isRecording: false,
              blob,
              duration: Math.floor(duration),
            }));
            setShowPreview(true);
          }
        },
        onError: (error) => {
          console.error('Recording error:', error);
          toast({
            title: 'Recording failed',
            description: error.message,
            variant: 'destructive',
          });
          setState(prev => ({ ...prev, isRecording: false }));

          if (durationIntervalRef.current) {
            clearInterval(durationIntervalRef.current);
            durationIntervalRef.current = null;
          }
        },
      });

      await recorderRef.current.start();
    } catch (error) {
      console.error('Failed to start recording:', error);
      toast({
        title: 'Failed to start recording',
        description: 'Please check your microphone permissions',
        variant: 'destructive',
      });
    }
  }, [toast]);

  // Handle recording stop
  const stopRecording = useCallback(() => {
    if (recorderRef.current) {
      recorderRef.current.stop();
    }
  }, []);

  // Handle mouse/touch down
  const handlePointerDown = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (disabled || state.isUploading) return;

    e.preventDefault();
    setIsHolding(true);

    // Start recording after a short delay to prevent accidental recordings
    holdTimeoutRef.current = setTimeout(() => {
      startRecording();
    }, 200);
  }, [disabled, state.isUploading, startRecording]);

  // Handle mouse/touch up
  const handlePointerUp = useCallback(() => {
    setIsHolding(false);

    // Clear the start timeout if still pending
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }

    // Stop recording if it's active
    if (state.isRecording) {
      stopRecording();
    }
  }, [state.isRecording, stopRecording]);

  // Handle playback toggle
  const togglePlayback = useCallback(() => {
    if (!state.blob) return;

    if (!audioRef.current) {
      // Create audio element from blob
      const audioUrl = URL.createObjectURL(state.blob);
      audioRef.current = new Audio(audioUrl);

      audioRef.current.addEventListener('ended', () => {
        setState(prev => ({ ...prev, isPlaying: false, playbackProgress: 0 }));
        if (playbackIntervalRef.current) {
          clearInterval(playbackIntervalRef.current);
          playbackIntervalRef.current = null;
        }
      });

      audioRef.current.addEventListener('error', (e) => {
        console.error('Playback error:', e);
        setState(prev => ({ ...prev, isPlaying: false }));
      });
    }

    if (state.isPlaying) {
      // Pause playback
      audioRef.current.pause();
      setState(prev => ({ ...prev, isPlaying: false }));

      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current);
        playbackIntervalRef.current = null;
      }
    } else {
      // Start playback
      audioRef.current.play();
      setState(prev => ({ ...prev, isPlaying: true }));

      // Update progress
      playbackIntervalRef.current = setInterval(() => {
        if (audioRef.current) {
          const progress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
          setState(prev => ({ ...prev, playbackProgress: progress }));
        }
      }, 100);
    }
  }, [state.blob, state.isPlaying]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    // Stop playback if playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    // Clean up recording
    if (recorderRef.current) {
      recorderRef.current.stop();
    }

    // Reset state
    setState({
      isRecording: false,
      isPaused: false,
      isUploading: false,
      isPlaying: false,
      duration: 0,
      amplitude: 0,
      blob: null,
      waveform: [],
      playbackProgress: 0,
    });

    setShowPreview(false);
    setIsHolding(false);

    // Clear intervals
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
    if (playbackIntervalRef.current) {
      clearInterval(playbackIntervalRef.current);
      playbackIntervalRef.current = null;
    }

    onCancel?.();
  }, [onCancel]);

  // Handle send
  const handleSend = useCallback(async () => {
    if (!state.blob) return;

    setState(prev => ({ ...prev, isUploading: true }));

    try {
      // Generate message ID upfront
      const messageId = crypto.randomUUID();

      // First create the message using sendMessage if available
      if (onSendMessage) {
        // Create empty message with attachment metadata for voice-only message
        const voiceMessageInfo = {
          hasAttachments: true,
          attachmentCount: 1,
          messageType: 'voice',
          messageId: messageId,
        };
        console.log('🎤 VoiceRecorder calling onSendMessage with:', voiceMessageInfo);
        await onSendMessage('', voiceMessageInfo);
      }

      // Convert blob to base64
      const base64 = await VoiceRecorderUtil.blobToBase64(state.blob);

      // Get auth session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      // Upload voice note using edge function (modified to not create message if we already did)
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/voice-notes`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            room_name: roomName,
            duration: state.duration,
            file_data: base64,
            mime_type: state.blob.type || 'audio/mp4',
            file_size: state.blob.size,
            waveform_data: state.waveform,
            entity_id: entityId,
            message_id: onSendMessage ? messageId : undefined, // Pass message ID if we created it
            skip_message_creation: !!onSendMessage, // Tell edge function to skip message creation
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to upload voice note');
      }

      const data = await response.json();

      toast({
        title: 'Voice note sent',
        description: `Duration: ${formatDuration(state.duration)}`,
      });

      // Reset state
      setState({
        isRecording: false,
        isPaused: false,
        isUploading: false,
        isPlaying: false,
        duration: 0,
        amplitude: 0,
        blob: null,
        waveform: [],
        playbackProgress: 0,
      });

      setShowPreview(false);

      // Pass attachment data for immediate display
      const attachmentData = {
        attachment_id: data.attachment_id,
        file_type: 'voice_note',
        file_url: data.file_url || '',
        signed_url: data.signed_url || '',
        file_name: 'Voice Note',
        mime_type: state.blob.type || 'audio/mp4',
        file_size: state.blob.size,
        duration: data.duration || state.duration,
        metadata: {
          waveform: data.waveform_data || state.waveform
        }
      };

      // Use the message ID we generated or the one from the server
      const finalMessageId = onSendMessage ? messageId : data.message_id;
      onRecordingComplete?.(finalMessageId, attachmentData);
    } catch (error) {
      console.error('Failed to send voice note:', error);
      toast({
        title: 'Failed to send',
        description: error instanceof Error ? error.message : 'Please try again',
        variant: 'destructive',
      });
      setState(prev => ({ ...prev, isUploading: false }));
    }
  }, [state.blob, state.duration, state.waveform, roomName, entityId, onRecordingComplete, onSendMessage, toast]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (recorderRef.current) {
        recorderRef.current.stop();
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current);
      }
      if (holdTimeoutRef.current) {
        clearTimeout(holdTimeoutRef.current);
      }
    };
  }, []);

  // Add global mouse/touch up listeners
  useEffect(() => {
    const handleGlobalUp = () => {
      if (isHolding) {
        handlePointerUp();
      }
    };

    window.addEventListener('mouseup', handleGlobalUp);
    window.addEventListener('touchend', handleGlobalUp);

    return () => {
      window.removeEventListener('mouseup', handleGlobalUp);
      window.removeEventListener('touchend', handleGlobalUp);
    };
  }, [isHolding, handlePointerUp]);

  return (
    <div className={cn('relative inline-block', className)}>
      {/* Fixed size container to prevent layout shifts */}
      <div className="w-10 h-10 relative">
        <AnimatePresence mode="wait">
          {!showPreview ? (
            // Recording button and live visualization
            <motion.div
              key="recorder"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="absolute inset-0"
            >
              {state.isRecording ? (
                // Recording state - keep button same size, show indicator as absolute overlay
                <>
                  {/* Mic button stays in place but shows recording state */}
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onMouseUp={stopRecording}
                    onTouchEnd={stopRecording}
                    className="w-full h-full rounded-full bg-red-500 text-white shadow-lg flex items-center justify-center"
                  >
                    <StopCircle className="h-5 w-5" />

                    {/* Pulse effect */}
                    <motion.div
                      className="absolute inset-0 rounded-full bg-red-400"
                      animate={{
                        scale: [1, 1.4, 1],
                        opacity: [0.5, 0, 0.5]
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    />
                  </motion.button>

                  {/* Recording overlay - responsive sizing */}
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0, y: 5 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-gradient-to-r from-red-500/95 to-red-600/95 backdrop-blur-sm rounded-full px-3 py-1.5 shadow-2xl z-50 whitespace-nowrap max-w-[200px]"
                >
                  {/* Live frequency visualization */}
                  <div className="flex items-center gap-[2px] h-8">
                    {liveAmplitudeBars.slice(-20).map((amplitude, i) => (
                      <motion.div
                        key={i}
                        className="w-[2px] bg-white/90 rounded-full"
                        animate={{
                          height: `${Math.max(4, amplitude * 28)}px`,
                          opacity: amplitude > 0.1 ? 0.9 : 0.4,
                        }}
                        transition={{
                          duration: 0.05,
                          ease: "easeOut"
                        }}
                      />
                    ))}
                  </div>

                  {/* Recording dot */}
                  <motion.div
                    className="w-2 h-2 bg-white rounded-full"
                    animate={{
                      scale: [1, 1.3, 1],
                      opacity: [1, 0.6, 1],
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  />

                  {/* Duration */}
                  <span className="text-sm font-bold text-white min-w-[45px] tabular-nums">
                    {formatDuration(state.duration)}
                  </span>
                </motion.div>
                </>
              ) : (
                // Idle state - just the mic button
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onMouseDown={handlePointerDown}
                  onTouchStart={handlePointerDown}
                  disabled={disabled || state.isUploading}
                  className={cn(
                    "w-full h-full rounded-full transition-all duration-200 flex items-center justify-center",
                    "bg-gradient-to-br from-primary/10 to-primary/20 hover:from-primary/20 hover:to-primary/30",
                    "border border-primary/20 hover:border-primary/30",
                    "shadow-sm hover:shadow-md",
                    isHolding && "bg-primary/30",
                    disabled && "opacity-50 cursor-not-allowed"
                  )}
                >
                  <Mic className={cn(
                    "h-5 w-5 transition-colors",
                    isHolding ? "text-primary" : "text-primary/70"
                  )} />

                {/* Holding animation */}
                {isHolding && (
                  <motion.div
                    className="absolute inset-0 rounded-full border-2 border-primary/50"
                    initial={{ scale: 1, opacity: 1 }}
                    animate={{ scale: 1.5, opacity: 0 }}
                    transition={{ duration: 0.5 }}
                  />
                )}
                </motion.button>
              )}
            </motion.div>
          ) : (
            // Preview and send UI - Still just the mic button, with floating preview
            <motion.div
              key="preview"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="absolute inset-0"
            >
              {/* Keep the mic button in DOM but transform it to send button */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSend}
                disabled={state.isUploading || state.isPlaying}
                className={cn(
                  "w-full h-full rounded-full transition-all flex items-center justify-center",
                  "bg-primary text-primary-foreground shadow-lg",
                  "hover:bg-primary/90",
                  (state.isUploading || state.isPlaying) && "opacity-50 cursor-not-allowed"
                )}
              >
              {state.isUploading ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  <Send className="h-5 w-5" />
                </motion.div>
              ) : (
                <Send className="h-5 w-5" />
              )}
              </motion.button>

              {/* Preview floating panel - responsive sizing */}
              <motion.div
                initial={{ scale: 0.9, opacity: 0, y: 5 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 5 }}
                className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-background/95 backdrop-blur-md border border-border/50 rounded-2xl p-2 shadow-2xl w-[260px] max-w-[90vw] z-50"
              >
              {/* Compact waveform with playback controls */}
              <div className="flex items-center gap-2">
                {/* Play/Pause button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={togglePlayback}
                  className="p-1.5 rounded-full bg-primary/10 hover:bg-primary/20 transition-colors flex-shrink-0"
                >
                  {state.isPlaying ? (
                    <Pause className="h-3.5 w-3.5 text-primary" />
                  ) : (
                    <Play className="h-3.5 w-3.5 text-primary" />
                  )}
                </motion.button>

                {/* Waveform visualization */}
                <div className="flex-1 min-w-0">
                  <div
                    className="relative h-8 cursor-pointer"
                    onClick={(e) => {
                      if (!audioRef.current || !state.blob) return;
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = e.clientX - rect.left;
                      const percentage = (x / rect.width) * 100;
                      const newTime = (percentage / 100) * state.duration;

                      if (audioRef.current) {
                        audioRef.current.currentTime = newTime;
                        setState(prev => ({ ...prev, playbackProgress: percentage }));
                      }
                    }}
                  >
                    <div className="absolute inset-0 flex items-center gap-[1px]">
                      {(state.waveform.length > 0 ? state.waveform.slice(0, 25) : Array(25).fill(0.3)).map((amplitude, i) => {
                        const barProgress = (i / (state.waveform.length || 35)) * 100;
                        const isPlayed = barProgress <= state.playbackProgress;

                        return (
                          <motion.div
                            key={i}
                            className={cn(
                              "flex-1 rounded-full transition-colors duration-100",
                              isPlayed ? "bg-primary" : "bg-primary/30"
                            )}
                            initial={{ height: 0 }}
                            animate={{
                              height: `${Math.max(3, (amplitude || 0.3) * 24)}px`,
                            }}
                            transition={{
                              delay: showPreview ? i * 0.005 : 0,
                              duration: 0.2,
                            }}
                          />
                        );
                      })}
                    </div>

                    {/* Progress line */}
                    {state.playbackProgress > 0 && (
                      <motion.div
                        className="absolute top-0 bottom-0 w-0.5 bg-primary"
                        style={{ left: `${state.playbackProgress}%` }}
                        initial={{ scaleY: 0 }}
                        animate={{ scaleY: 1 }}
                      />
                    )}
                  </div>

                  {/* Time display - more compact */}
                  <div className="flex justify-between">
                    <span className="text-[9px] text-muted-foreground tabular-nums">
                      {state.isPlaying || state.playbackProgress > 0
                        ? formatDuration(Math.floor((state.playbackProgress / 100) * state.duration))
                        : "0:00"}
                    </span>
                    <span className="text-[9px] text-muted-foreground tabular-nums">
                      {formatDuration(state.duration)}
                    </span>
                  </div>
                </div>

                {/* Delete button - smaller */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleCancel}
                  className="p-1.5 rounded-full hover:bg-destructive/10 transition-colors flex-shrink-0"
                >
                  <Trash2 className="h-3.5 w-3.5 text-destructive/70" />
                </motion.button>
              </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};